import api from './authService';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  content_image?: string | null;
  content_image_url?: string | null;
  content_image_urls?: string[]; // multiple attachments in one message bubble
  /** When true, show "Search web" / "Stop" buttons below this message */
  requiresChoice?: boolean;
}

export interface ChatResponse {
  success: boolean;
  chat_id?: number;
  reply: string;
  usage?: { input_tokens?: number; output_tokens?: number };
  sourcesUsed?: string | null;
  webUsed?: boolean;
  /** When true, the reply asks user to choose between searching the web or stopping */
  requiresChoice?: boolean;
}

export interface ChatListItem {
  id: number;
  title: string | null;
  created_at: string;
  updated_at: string;
  last_message: string | null;
}

export interface ChatsListResponse {
  success: boolean;
  chats: ChatListItem[];
}

export interface ChatDetailResponse {
  success: boolean;
  chat: { id: number; title: string | null; created_at: string; updated_at: string };
  messages: ChatMessage[];
}

export interface SourcesResponse {
  success: boolean;
  folder: string;
  files: { name: string }[];
}

function getApiBase(): string {
  const b = (api as { defaults?: { baseURL?: string } }).defaults?.baseURL || '';
  return b.replace(/\/api\/?$/, '') || window.location.origin;
}

export const chatbotService = {
  async sendMessage(
    message: string,
    history: ChatMessage[],
    chatId?: number | null,
    files?: File[] | File | null
  ): Promise<ChatResponse> {
    // Longer timeout for chat (PDF + AI can take 60–90s). Reduces "network error" when server is slow.
    const chatTimeout = 90000;
    const filesArr: File[] = files
      ? (Array.isArray(files) ? files : [files])
      : [];
    if (filesArr.length > 0) {
      const form = new FormData();
      form.append('message', message);
      if (chatId != null) form.append('chat_id', String(chatId));
      form.append('history', JSON.stringify(history.map((m) => ({ role: m.role, content: m.content }))));
      // Append up to 10 files; route accepts fields: image/file/pdf/document
      filesArr.slice(0, 10).forEach((file) => {
        const name = (file.name || '').toLowerCase();
        const isPdf = name.endsWith('.pdf') || file.type === 'application/pdf';
        form.append(isPdf ? 'pdf' : 'image', file);
      });
      const response = await api.post<ChatResponse>('/chatbot/chat', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: chatTimeout,
      });
      return response.data;
    }
    const response = await api.post<ChatResponse>('/chatbot/chat', {
      message,
      chat_id: chatId ?? undefined,
      history: history.map((m) => ({ role: m.role, content: m.content })),
    }, { timeout: chatTimeout });
    return response.data;
  },

  async getChats(): Promise<ChatsListResponse> {
    const response = await api.get<ChatsListResponse>('/chatbot/chats');
    return response.data;
  },

  async getChat(id: number): Promise<ChatDetailResponse> {
    const response = await api.get<ChatDetailResponse>(`/chatbot/chats/${id}`);
    return response.data;
  },

  async getSources(): Promise<SourcesResponse> {
    const response = await api.get<SourcesResponse>('/chatbot/sources');
    return response.data;
  },

  getImageUrl(contentImagePath: string | null | undefined): string | null {
    if (!contentImagePath) return null;
    const base = getApiBase();
    return `${base}/${contentImagePath.replace(/^\/+/, '')}`;
  },
};
